import type { paperStrategy } from '@whaletail/signal-engine';
import { formatPercent, formatUsd } from '../../lib/format';
import { allRowsIdentical, SHADOW_BUST_NOTE, shadowRunsLine } from './shadowRuns';
import styles from './Btc15ShadowSizingPolicyComparison.module.css';

const SIZING_LABEL: Record<string, string> = {
  'quarter-kelly': 'Quarter-Kelly (live)',
  'confidence-scaled': 'Confidence-scaled (experimental)',
  'profit-scaled': 'Profit-scaled (experimental)',
};

function labelFor(sizingId: string): string {
  return SIZING_LABEL[sizingId] ?? sizingId;
}

function teaser(eligibleRounds: number): string {
  return eligibleRounds > 0 ? `${eligibleRounds} rounds replayed` : '—';
}

/**
 * Retrospective-only: replays several sizing-policy variants
 * (quarter-Kelly plus the two opt-in `v2/sizingPolicies.ts` variants) against
 * the same real settled "V1 + take-profit" rounds, using whatever Kalshi
 * quote path the archive actually recorded — exit policy held fixed at
 * take-profit-default, so only the sizing axis varies (the mirror image of
 * Btc15ShadowExitPolicyComparison, which varies exit policy with sizing held
 * fixed). This never changes which sizing rule is actually live —
 * calculateAllocation and every live P&L replay are completely unaffected by
 * anything shown here. Collapsed by default, same pattern as
 * Btc15ShadowExitPolicyComparison/Btc15Diagnostics.
 */
export function Btc15ShadowSizingPolicyComparison({
  eligibleRounds,
  sizingDollarPnl,
}: {
  eligibleRounds: number;
  sizingDollarPnl: paperStrategy.ShadowPolicyDollarPnlSummary[];
}) {
  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span>Shadowed sizing-policy comparison (retrospective)</span>
        <span className={styles.teaser}>{teaser(eligibleRounds)}</span>
      </summary>
      <div className={styles.body}>
        <p className={styles.note}>
          Replays the same settled rounds and archived Kalshi quotes under each sizing
          policy, with exit policy held fixed at the live take-profit config — purely
          retrospective, and never changes which sizing rule is actually live.
          Confidence-scaled and profit-scaled are experimental and opt-in only (see
          packages/signal-engine/src/v2/README.md); neither is used by any live P&amp;L
          calculation today. {SHADOW_BUST_NOTE}
        </p>
        {sizingDollarPnl.length === 0 || eligibleRounds === 0 ? (
          <p className={styles.placeholder}>No settled, funded rounds to replay yet.</p>
        ) : (
          <div className={styles.table}>
            {allRowsIdentical(sizingDollarPnl) && (
              <p className={styles.note}>
                Every policy placed exactly the same bets: the experimental ones differ
                only by raising the $20 cap, and that hasn&apos;t changed a single bet
                here.
              </p>
            )}
            {sizingDollarPnl.map((summary) => (
              <div className={styles.row} key={summary.identity.sizingId}>
                <span className={styles.policyLabel}>
                  {labelFor(summary.identity.sizingId)}
                </span>
                <span className={styles.stat}>{summary.trades} trades</span>
                <span className={styles.stat}>{formatUsd(summary.stake)} staked</span>
                <span
                  className={`${styles.stat} ${summary.pnl >= 0 ? styles.pnlPositive : styles.pnlNegative}`}
                >
                  {formatUsd(summary.pnl)} P&amp;L after fees (
                  {formatPercent(summary.roi, 0)} ROI)
                </span>
                <span className={styles.stat}>{shadowRunsLine(summary)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}
